/**
 * G2eGente - Avaliação de Desempenho do Funcionário
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeesAPI, performanceCyclesAPI, performanceReviewsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Loader2,
  Save,
  ClipboardCheck,
  Star,
  Brain,
  Plus,
  X,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SCORE_OPTIONS = [
  { value: 1, label: '1 - Abaixo do esperado', color: 'bg-red-500' },
  { value: 2, label: '2 - Parcialmente atende', color: 'bg-orange-500' },
  { value: 3, label: '3 - Atende às expectativas', color: 'bg-yellow-500' },
  { value: 4, label: '4 - Supera expectativas', color: 'bg-green-500' },
  { value: 5, label: '5 - Excepcional', color: 'bg-emerald-500' }
];

export default function EmployeeEvaluate() {
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [templates, setTemplates] = useState({ competencias_tecnicas: [], competencias_comportamentais: [] });
  
  const [selectedCycle, setSelectedCycle] = useState(searchParams.get('cycle') || '');
  const [existingReviewId, setExistingReviewId] = useState(searchParams.get('review') || null);
  const [tipo, setTipo] = useState('gestor');
  
  const [tecnicas, setTecnicas] = useState([]);
  const [comportamentais, setComportamentais] = useState([]);
  const [pontosFortes, setPontosFortes] = useState(['']);
  const [pontosMelhoria, setPontosMelhoria] = useState(['']);
  const [comentarioGeral, setComentarioGeral] = useState('');

  useEffect(() => {
    fetchData();
  }, [employeeId]);

  // Carregar dados do rascunho existente se houver review ID
  useEffect(() => {
    if (existingReviewId && templates.competencias_tecnicas.length > 0) {
      loadExistingReview();
    }
  }, [existingReviewId, templates]);

  const loadExistingReview = async () => {
    if (!existingReviewId) return;
    
    try {
      const response = await performanceReviewsAPI.getById(existingReviewId);
      const review = response.data;
      
      if (review.status !== 'rascunho') {
        toast.info('Esta avaliação já foi finalizada e não pode ser editada');
        return;
      }
      
      // Preencher os dados do rascunho
      if (review.competencias_tecnicas?.length > 0) {
        setTecnicas(review.competencias_tecnicas);
      }
      if (review.competencias_comportamentais?.length > 0) {
        setComportamentais(review.competencias_comportamentais);
      }
      if (review.pontos_fortes?.length > 0) {
        setPontosFortes(review.pontos_fortes);
      }
      if (review.pontos_melhoria?.length > 0) {
        setPontosMelhoria(review.pontos_melhoria);
      }
      if (review.comentario_geral) {
        setComentarioGeral(review.comentario_geral);
      }
      if (review.tipo) {
        setTipo(review.tipo);
      }
      
      toast.success('Rascunho carregado. Continue sua avaliação.');
    } catch (error) {
      console.error('Erro ao carregar rascunho:', error);
      toast.error('Erro ao carregar rascunho da avaliação');
    }
  };

  const fetchData = async () => {
    try {
      let empData, cyclesData, templatesData;
      
      try {
        const empRes = await employeesAPI.getById(employeeId);
        empData = empRes.data;
      } catch (empError) {
        console.error('Erro ao carregar funcionário:', empError);
        if (empError.response?.status === 404) {
          toast.error('Funcionário não encontrado');
        } else {
          toast.error('Erro ao carregar funcionário');
        }
        navigate('/company/employees');
        return;
      }
      
      const [cyclesRes, templatesRes] = await Promise.all([
        performanceCyclesAPI.getAll({ status: 'ativo' }),
        performanceReviewsAPI.getTemplates()
      ]);
      
      setEmployee(empData);
      setCycles(cyclesRes.data);
      setTemplates(templatesRes.data);
      
      // Inicializar competências com valores vazios
      setTecnicas(templatesRes.data.competencias_tecnicas.map(c => ({
        nome: c.nome,
        pontuacao: null,
        observacao: ''
      })));
      setComportamentais(templatesRes.data.competencias_comportamentais.map(c => ({
        nome: c.nome,
        pontuacao: null,
        observacao: ''
      })));
      
      // Pre-select first active cycle
      if (cyclesRes.data.length > 0 && !searchParams.get('cycle')) {
        setSelectedCycle(cyclesRes.data[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
      navigate('/company/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (type, index, value) => {
    if (type === 'tecnica') {
      const updated = [...tecnicas];
      updated[index].pontuacao = value;
      setTecnicas(updated);
    } else {
      const updated = [...comportamentais];
      updated[index].pontuacao = value;
      setComportamentais(updated);
    }
  };

  const handleObservacaoChange = (type, index, value) => {
    if (type === 'tecnica') {
      const updated = [...tecnicas];
      updated[index].observacao = value;
      setTecnicas(updated);
    } else {
      const updated = [...comportamentais];
      updated[index].observacao = value;
      setComportamentais(updated);
    }
  };

  const addPonto = (type) => {
    if (type === 'forte') {
      setPontosFortes([...pontosFortes, '']);
    } else {
      setPontosMelhoria([...pontosMelhoria, '']);
    }
  };

  const removePonto = (type, index) => {
    if (type === 'forte') {
      setPontosFortes(pontosFortes.filter((_, i) => i !== index));
    } else {
      setPontosMelhoria(pontosMelhoria.filter((_, i) => i !== index));
    }
  };

  const updatePonto = (type, index, value) => {
    if (type === 'forte') {
      const updated = [...pontosFortes];
      updated[index] = value;
      setPontosFortes(updated);
    } else {
      const updated = [...pontosMelhoria];
      updated[index] = value;
      setPontosMelhoria(updated);
    }
  };

  const calculatePreview = () => {
    const tecScores = tecnicas.filter(t => t.pontuacao).map(t => t.pontuacao);
    const compScores = comportamentais.filter(c => c.pontuacao).map(c => c.pontuacao);
    
    const mediaTec = tecScores.length > 0 ? tecScores.reduce((a, b) => a + b, 0) / tecScores.length : 0;
    const mediaComp = compScores.length > 0 ? compScores.reduce((a, b) => a + b, 0) / compScores.length : 0;
    
    const cycle = cycles.find(c => c.id === selectedCycle);
    if (!cycle) return { mediaTec: 0, mediaComp: 0, mediaGeral: 0 };
    
    const pesoTec = cycle.peso_competencias_tecnicas / 100;
    const pesoComp = cycle.peso_competencias_comportamentais / 100;
    const pesoDISC = cycle.peso_disc / 100;
    const discScore = employee?.disc_profile ? 4.0 : 3.0;
    
    const mediaGeral = (mediaTec * pesoTec) + (mediaComp * pesoComp) + (discScore * pesoDISC);
    
    return { 
      mediaTec: mediaTec.toFixed(2), 
      mediaComp: mediaComp.toFixed(2), 
      mediaGeral: mediaGeral.toFixed(2) 
    };
  };

  const handleSubmit = async (status) => {
    if (!selectedCycle) {
      toast.error('Selecione um ciclo de avaliação');
      return;
    }
    
    const tecnicasPreenchidas = tecnicas.filter(t => t.pontuacao);
    const comportamentaisPreenchidas = comportamentais.filter(c => c.pontuacao);
    
    if (tecnicasPreenchidas.length === 0 || comportamentaisPreenchidas.length === 0) {
      toast.error('Preencha pelo menos uma competência de cada tipo');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        cycle_id: selectedCycle,
        employee_id: employeeId,
        tipo,
        competencias_tecnicas: tecnicasPreenchidas,
        competencias_comportamentais: comportamentaisPreenchidas,
        pontos_fortes: pontosFortes.filter(p => p.trim()),
        pontos_melhoria: pontosMelhoria.filter(p => p.trim()),
        comentario_geral: comentarioGeral,
        status
      };
      
      // Se existe um review ID (rascunho), atualizar em vez de criar
      if (existingReviewId) {
        await performanceReviewsAPI.update(existingReviewId, data);
        toast.success(status === 'finalizado' ? 'Avaliação finalizada!' : 'Rascunho atualizado');
      } else {
        await performanceReviewsAPI.create(data);
        toast.success(status === 'finalizado' ? 'Avaliação finalizada!' : 'Avaliação salva como rascunho');
      }
      navigate('/company/performance');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar avaliação');
    } finally {
      setSaving(false);
    }
  };

  const preview = calculatePreview();
  const selectedCycleData = cycles.find(c => c.id === selectedCycle);

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="employee-evaluate">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/company/employees')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Avaliação de Desempenho</h1>
            <p className="text-slate-600">{employee?.nome} - {employee?.cargo}</p>
          </div>
          {employee?.disc_profile && (
            <Badge className="bg-purple-100 text-purple-700">
              <Brain className="w-3 h-3 mr-1" />
              DISC: {employee.disc_profile}
            </Badge>
          )}
        </div>

        {/* No Active Cycles Warning */}
        {cycles.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900">Nenhum ciclo de avaliação ativo</h3>
                  <p className="text-amber-700 text-sm mt-1">
                    Para realizar avaliações, é necessário criar e iniciar um ciclo de avaliação.
                  </p>
                  <Button 
                    onClick={() => navigate('/company/performance/cycles/new')}
                    className="mt-3 bg-amber-600 hover:bg-amber-700"
                    size="sm"
                  >
                    Criar Ciclo de Avaliação
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {cycles.length > 0 && (
          <>
            {/* Cycle Selection */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Configuração da Avaliação</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ciclo de Avaliação</Label>
                    <Select value={selectedCycle} onValueChange={setSelectedCycle}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o ciclo" />
                      </SelectTrigger>
                      <SelectContent>
                        {cycles.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.nome} ({c.periodo_inicio} a {c.periodo_fim})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Avaliação</Label>
                    <Select value={tipo} onValueChange={setTipo}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gestor">Avaliação do Gestor</SelectItem>
                        <SelectItem value="autoavaliacao">Autoavaliação</SelectItem>
                        <SelectItem value="pares">Avaliação de Pares</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {selectedCycleData && (
                  <div className="flex gap-2 text-xs text-slate-600 pt-2">
                    <Badge variant="outline">Técnicas: {selectedCycleData.peso_competencias_tecnicas}%</Badge>
                    <Badge variant="outline">Comportamentais: {selectedCycleData.peso_competencias_comportamentais}%</Badge>
                    <Badge variant="outline">DISC: {selectedCycleData.peso_disc}%</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Competências Técnicas */}
            <Card className="border-slate-200">
              <CardHeader className="border-b bg-blue-50">
                <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5" />
                  Competências Técnicas
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {tecnicas.map((comp, index) => (
                  <div key={index} className="p-4 border-b last:border-b-0">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-medium text-slate-900">{comp.nome}</h4>
                      {comp.pontuacao && (
                        <Badge className={SCORE_OPTIONS.find(s => s.value === comp.pontuacao)?.color + ' text-white'}>
                          {comp.pontuacao}/5
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2 mb-3">
                      {SCORE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => handleScoreChange('tecnica', index, opt.value)}
                          className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                            comp.pontuacao === opt.value
                              ? `border-blue-500 bg-blue-50 text-blue-700`
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-1">
                            <Star className={`w-4 h-4 ${comp.pontuacao === opt.value ? 'fill-blue-500' : ''}`} />
                            <span className="hidden sm:inline">{opt.value}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    <Textarea
                      placeholder="Observações (opcional)"
                      value={comp.observacao}
                      onChange={(e) => handleObservacaoChange('tecnica', index, e.target.value)}
                      className="text-sm"
                      rows={2}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Competências Comportamentais */}
            <Card className="border-slate-200">
              <CardHeader className="border-b bg-green-50">
                <CardTitle className="text-lg text-green-900 flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  Competências Comportamentais
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {comportamentais.map((comp, index) => (
                  <div key={index} className="p-4 border-b last:border-b-0">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-medium text-slate-900">{comp.nome}</h4>
                      {comp.pontuacao && (
                        <Badge className={SCORE_OPTIONS.find(s => s.value === comp.pontuacao)?.color + ' text-white'}>
                          {comp.pontuacao}/5
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2 mb-3">
                      {SCORE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => handleScoreChange('comportamental', index, opt.value)}
                          className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                            comp.pontuacao === opt.value
                              ? `border-green-500 bg-green-50 text-green-700`
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-1">
                            <Star className={`w-4 h-4 ${comp.pontuacao === opt.value ? 'fill-green-500' : ''}`} />
                            <span className="hidden sm:inline">{opt.value}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    <Textarea
                      placeholder="Observações (opcional)"
                      value={comp.observacao}
                      onChange={(e) => handleObservacaoChange('comportamental', index, e.target.value)}
                      className="text-sm"
                      rows={2}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Feedback Qualitativo */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Feedback Qualitativo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pontos Fortes */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-green-700">Pontos Fortes</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => addPonto('forte')}
                      className="text-green-600"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar
                    </Button>
                  </div>
                  {pontosFortes.map((ponto, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={ponto}
                        onChange={(e) => updatePonto('forte', index, e.target.value)}
                        placeholder="Ex: Excelente comunicação"
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      {pontosFortes.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removePonto('forte', index)}
                        >
                          <X className="w-4 h-4 text-slate-400" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pontos de Melhoria */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-amber-700">Pontos de Melhoria</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => addPonto('melhoria')}
                      className="text-amber-600"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar
                    </Button>
                  </div>
                  {pontosMelhoria.map((ponto, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={ponto}
                        onChange={(e) => updatePonto('melhoria', index, e.target.value)}
                        placeholder="Ex: Pode ser mais proativo"
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                      {pontosMelhoria.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removePonto('melhoria', index)}
                        >
                          <X className="w-4 h-4 text-slate-400" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Comentário Geral */}
                <div className="space-y-2">
                  <Label>Comentário Geral</Label>
                  <Textarea
                    value={comentarioGeral}
                    onChange={(e) => setComentarioGeral(e.target.value)}
                    placeholder="Escreva um feedback geral sobre o desempenho do funcionário..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Preview & Actions */}
            <Card className="border-cyan-200 bg-cyan-50">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Prévia da Avaliação</h4>
                    <div className="flex gap-4 text-sm">
                      <span>Técnica: <strong>{preview.mediaTec}</strong></span>
                      <span>Comportamental: <strong>{preview.mediaComp}</strong></span>
                      <span className="text-cyan-700">Geral: <strong>{preview.mediaGeral}/5</strong></span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => handleSubmit('rascunho')}
                      disabled={saving}
                    >
                      Salvar Rascunho
                    </Button>
                    <Button
                      onClick={() => handleSubmit('finalizado')}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Finalizar Avaliação
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
